import React, { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import StayCardV6 from "components/StayCard/StayCardV6";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import CommonLayoutV2 from "./CommonLayoutV2";
import { AppDispatch, RootState } from "redux/store";
import { getallupdateProperties } from "../../redux/reducers/Properties/PropertiesSlice";
import { checkExistingCompletion } from "utils/helpers/listingCompletionHelper";

export interface PropertyListProps {}

const PropertyList: FC<PropertyListProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { PropertyToEdit } = useSelector((state: RootState) => state.property);
  const { slug } = useParams();
  const id = slug;
  const propertyId = id;
  const RouteID = id;

  const [propertyData, setPropertyData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cachedPropertyType, setCachedPropertyType] = useState<string | null>(null);

  // Track step completion status
  const [stepCompletionStatus, setStepCompletionStatus] = useState({
    1: { completed: false },
    2: { completed: false },
    3: { completed: false },
    4: { completed: false },
    5: { completed: false },
    6: { completed: false },
    7: { completed: false },
    8: { completed: false },
    9: { completed: true, fieldsCompleted: 1, totalFields: 1 },
  });

  useEffect(() => {
    if (propertyId || id) {
      dispatch(getallupdateProperties({ propertyId })).then((response: any) => {
        setPropertyData(response.payload);
      });
    } else {
      console.log("No property ID found");
    }
  }, [dispatch, propertyId, id]);

  useEffect(() => {
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  // Load completion status from PropertyToEdit
  useEffect(() => {
    if (PropertyToEdit) {
      const existingCompletion = checkExistingCompletion(PropertyToEdit);

      setStepCompletionStatus((prev) => ({
        ...prev,
        ...existingCompletion,
      }));
    }
  }, [PropertyToEdit]);

  // Update cached property type when PropertyToEdit loads
  useEffect(() => {
    if (PropertyToEdit?.property_type?.name) {
      setCachedPropertyType(PropertyToEdit.property_type.name);
    }
  }, [PropertyToEdit?.property_type?.name]);

  const isApproved = propertyData?.property?.property_state === "approved";
  const property = propertyData?.property;

  // Calculate completion percentage
  const completedSteps = Object.values(stepCompletionStatus).filter(
    (step: any) => step.completed
  ).length;
  const totalSteps = Object.keys(stepCompletionStatus).length;
  const completionPercentage = Math.round((completedSteps / totalSteps) * 100);

  // NEW: Determine completion status
  const isFullyComplete = completionPercentage === 100;
  const isNearlyComplete = completionPercentage >= 80 && completionPercentage < 100;
  const isPartiallyComplete = completionPercentage >= 50 && completionPercentage < 80;
  const isMinimallyComplete = completionPercentage < 50;

  // NEW: Get dynamic banner configuration based on completion and approval status
  const getBannerConfig = () => {
    if (isApproved) {
      return {
        gradient: "bg-gradient-to-r from-green-500 to-emerald-600",
        icon: "approved",
        title: "Property Approved! ✓",
        message: "Your property is live and ready to receive bookings. Feel free to update details anytime!",
      };
    }

    if (isFullyComplete) {
      return {
        gradient: "bg-gradient-to-r from-[#F56948] to-[#E5593D]",
        icon: "success",
        title: "Congratulations! 🎉",
        message: "Your listing is complete and has been submitted for review. We'll notify you once it's approved!",
      };
    }

    if (isNearlyComplete) {
      return {
        gradient: "bg-gradient-to-r from-yellow-500 to-orange-500",
        icon: "progress",
        title: "Almost There! 🚀",
        message: "You're nearly done! Complete the remaining steps to submit your listing for review.",
      };
    }

    if (isPartiallyComplete) {
      return {
        gradient: "bg-gradient-to-r from-blue-500 to-indigo-600",
        icon: "progress",
        title: "Great Progress! 💪",
        message: "You're halfway there! Keep going to complete your listing and get it approved.",
      };
    }

    return {
      gradient: "bg-gradient-to-r from-purple-500 to-pink-600",
      icon: "start",
      title: "Let's Get Started! 🏠",
      message: "Complete all the required steps to publish your property and start receiving bookings.",
    };
  };

  const bannerConfig = getBannerConfig();

  // NEW: Get icon based on banner type
  const getBannerIcon = (iconType: string) => {
    switch (iconType) {
      case "approved":
        return (
          <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "success":
        return (
          <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "progress":
        return (
          <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "start":
        return (
          <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <CommonLayoutV2
      currentHref="/add-listing-9"
      PropertyID={RouteID}
      propertyType={cachedPropertyType}
      stepCompletionStatus={stepCompletionStatus}
    >
      <div className="space-y-8">
        {/* Success/Progress Banner */}
        {isLoading ? (
          <div className="h-32 bg-neutral-200 dark:bg-neutral-700 rounded-2xl animate-pulse" />
        ) : (
          <div className={`relative overflow-hidden rounded-2xl p-8 ${bannerConfig.gradient}`}>
            {/* Decorative circles */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-5 rounded-full -ml-24 -mb-24"></div>

            <div className="relative z-10 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  {getBannerIcon(bannerConfig.icon)}
                  <div>
                    <h2 className="text-3xl font-bold text-white">{bannerConfig.title}</h2>
                    <p className="text-white text-opacity-90 mt-2 text-base">
                      {bannerConfig.message}
                    </p>
                  </div>
                </div>
              </div>

              {/* Completion Badge */}
              <div className="hidden lg:flex flex-col items-center bg-white bg-opacity-20 rounded-2xl p-6 backdrop-blur-sm">
                <div className="relative w-24 h-24">
                  <svg className="transform -rotate-90 w-24 h-24">
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="white"
                      strokeOpacity="0.3"
                      strokeWidth="8"
                      fill="none"
                    />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="white"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${2 * Math.PI * 40 * (1 - completionPercentage / 100)}`}
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">{completionPercentage}%</span>
                  </div>
                </div>
                <p className="text-white text-sm mt-2 font-medium">Complete</p>
              </div>
            </div>
          </div>
        )}

        {/* NEW: Completion Progress Alert (only show if not fully complete and not approved) */}
        {!isFullyComplete && !isApproved && (
          <div className="bg-amber-50 dark:bg-amber-900/10 rounded-2xl border border-amber-200 dark:border-amber-800 p-6">
            <div className="flex gap-4">
              <svg className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <h4 className="text-base font-semibold text-amber-900 dark:text-amber-200 mb-2">
                  Complete Your Listing
                </h4>
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  Your listing is <strong>{completionPercentage}% complete</strong>. You need to complete{" "}
                  <strong>all required steps (100%)</strong> before your property can be submitted for review and approval.
                </p>
                <div className="mt-3">
                  <ButtonSecondary href={`/add-listing-1/${slug}`} className="text-sm">
                    Complete Missing Steps →
                  </ButtonSecondary>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Property Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Quick Stats Card 1 */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Property Type</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isLoading ? "..." : property?.is_shared_property ? "Shared Property" : "Entire Property"}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Stats Card 2 */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Rooms</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {isLoading ? "..." : property?.rooms?.length || "0"} {property?.rooms?.length === 1 ? "Room" : "Rooms"}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Stats Card 3 */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                  {isLoading ? "..." : (property?.property_state?.replace(/_/g, " ") || "Pending")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Your Listing Preview</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                This is how your property will appear to potential guests
              </p>
            </div>
          </div>

          {/* Property Card Preview */}
          <div className="max-w-md mx-auto">
            {isLoading ? (
              <div className="h-96 bg-neutral-200 dark:bg-neutral-700 rounded-2xl animate-pulse" />
            ) : (
              propertyData && <StayCardV6 className="shadow-lg" listingdata={propertyData} />
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <ButtonSecondary href={`/add-listing-1/${slug}`} className="w-full sm:w-auto">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                <span className="ml-3">Edit Listing</span>
              </ButtonSecondary>

              {/* Only show "View Live Listing" if approved */}
              {isApproved && (
                <ButtonPrimary href={`/listing-stay-detail/${propertyId}`} className="w-full sm:w-auto">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  <span className="ml-3">View Live Listing</span>
                </ButtonPrimary>
              )}

              {/* Show preview for non-approved listings */}
              {!isApproved && (
                <ButtonPrimary href={`/listing-stay-detail/${propertyId}`} className="w-full sm:w-auto">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                  <span className="ml-3">Preview Listing</span>
                </ButtonPrimary>
              )}
            </div>
          </div>
        </div>

        {/* Next Steps Card */}
        <div className="bg-blue-50 dark:bg-blue-900/10 rounded-2xl border border-blue-200 dark:border-blue-800 p-6">
          <div className="flex gap-4">
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <h4 className="text-base font-semibold text-blue-900 dark:text-blue-200 mb-2">What's Next?</h4>
              <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
                {isApproved ? (
                  <>
                    <li className="flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Your property is now visible to guests and accepting bookings</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Keep your calendar and pricing up to date for best results</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Respond promptly to booking inquiries to maintain a high rating</span>
                    </li>
                  </>
                ) : isFullyComplete ? (
                  <>
                    <li className="flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Our team will review your listing within 24-48 hours</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                      </svg>
                      <span>You'll receive an email notification once your property is approved</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>In the meantime, you can still edit and improve your listing details</span>
                    </li>
                  </>
                ) : (
                  <>
                    <li className="flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Complete all {totalSteps} steps to reach 100% and submit for review</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>You have {totalSteps - completedSteps} remaining {totalSteps - completedSteps === 1 ? 'step' : 'steps'} to complete</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>Once 100% complete, your listing will be submitted for review</span>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </CommonLayoutV2>
  );
};

export default PropertyList;